import { Button } from "@/components/generic/Button";
import { Select, SelectItem } from "@/components/generic/Select";
import { FieldMappings } from "@/components/integrations/FieldMapping";
import { useIntegrationSetup } from "@/components/integrations/IntegrationSetupContext";
import { useHorizontalScrollSync } from "@/hooks/use-scroll-sync";
import {
  getIntegrationDefaultMappingsQueryOptions,
  getIntegrationFieldsQueryOptions,
  useConnectIntegrationMutation,
} from "@/queries/integrations";
import {
  FieldsForm,
  fieldsFormSchema,
  SingleFieldMapping,
} from "@/types/fieldsFormSchema";
import {
  ProviderConnectionInfo,
  ProviderConnectionInfoField,
} from "@/types/integrations";
import { mapExportInputs } from "@/utils/integration-helpers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Loader2, LoaderCircle } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import z from "zod";
import { StickyElement } from "../layout/StickyElement";

export interface FieldsStepProps {
  /** Called when the step completes and should proceed to next */
  onNext?: (updatedConnectionInfo?: ProviderConnectionInfo) => void;
  /** Called when user goes back */
  onBack?: () => void;
  /** Pre-loaded integration fields */
  integrationFieldsInitialData?: ProviderConnectionInfoField[];
  /** Pre-loaded VIRK fields */
  virkFieldsInitialData?: ProviderConnectionInfoField[];
}

export function FieldsStep({
  onNext,
  onBack,
  integrationFieldsInitialData = [],
  virkFieldsInitialData = [],
}: FieldsStepProps) {
  const {
    integration,
    mode,
    connectionInfo,
    setConnectionInfo,
    getSessionKey,
  } = useIntegrationSetup();

  const {
    data: integrationFields = integrationFieldsInitialData,
    isLoading: isLoadingIntegrationFields,
  } = useQuery(
    getIntegrationFieldsQueryOptions(integration, connectionInfo ?? undefined),
  );
  const {
    data: virkFields = virkFieldsInitialData,
    isLoading: isLoadingVirkFields,
  } = useQuery(getIntegrationFieldsQueryOptions("VIRK"));

  const connectMutation = useConnectIntegrationMutation(
    mode === "setup" ? "post" : "put",
  );

  const sessionStoredFields = useMemo<
    z.infer<typeof fieldsFormSchema>["fieldMappings"]
  >(
    () =>
      JSON.parse(sessionStorage.getItem(getSessionKey("fields")) ?? "null") ??
      ({} as z.infer<typeof fieldsFormSchema>["fieldMappings"]),
    [getSessionKey],
  );

  const stickyRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  useHorizontalScrollSync(stickyRef, containerRef);

  const [mappingGroup, setMappingGroup] = useState<string | null>(null);
  const [pendingStepChange, setPendingStepChange] = useState(false);
  const hasInitializedForm = useRef(false);

  // Get default mappings for the integration
  const { data: defaultMappings } = useQuery(
    getIntegrationDefaultMappingsQueryOptions(
      integration,
      connectionInfo ?? undefined,
    ),
  );

  // Form setup
  const {
    handleSubmit,
    control,
    formState: { isValid },
    reset,
    watch,
  } = useForm({
    mode: "onChange",
    resolver: zodResolver(fieldsFormSchema),
    defaultValues: {
      fieldMappings: {},
    },
  });

  const mappings = watch();

  const { virkFieldsByEntity, requiredVirkFieldsByEntity } = useMemo(() => {
    const virkFieldsByEntity = new Map<
      string,
      Map<string, ProviderConnectionInfoField>
    >();
    const requiredVirkFields: ProviderConnectionInfoField[] = [];
    const requiredVirkFieldsByEntity = new Map<
      string,
      Map<string, ProviderConnectionInfoField>
    >();

    for (const field of virkFields) {
      let entityFields = virkFieldsByEntity.get(field.entity);
      if (!entityFields) {
        entityFields = new Map();
        virkFieldsByEntity.set(field.entity, entityFields);
      }
      entityFields.set(field.fieldId, field);

      if (field.required) {
        requiredVirkFields.push(field);

        let requiredVirkEntityFields = requiredVirkFieldsByEntity.get(
          field.entity,
        );
        if (!requiredVirkEntityFields) {
          requiredVirkEntityFields = new Map();
          requiredVirkFieldsByEntity.set(
            field.entity,
            requiredVirkEntityFields,
          );
        }
        requiredVirkEntityFields.set(field.fieldId, field);
      }
    }

    return {
      virkFieldsByEntity,
      requiredVirkFields,
      requiredVirkFieldsByEntity,
    };
  }, [virkFields]);

  const { integrationFieldsByVirkEntity } = useMemo(() => {
    const integrationFieldsByIntegrationEntity = new Map<
      string,
      Map<string, ProviderConnectionInfoField>
    >();

    const integrationFieldsByVirkEntity = new Map<
      string,
      Map<string, ProviderConnectionInfoField>
    >();

    for (const field of integrationFields) {
      let integrationEntityFields = integrationFieldsByIntegrationEntity.get(
        field.entity,
      );
      if (!integrationEntityFields) {
        integrationEntityFields = new Map();
        integrationFieldsByIntegrationEntity.set(
          field.entity,
          integrationEntityFields,
        );
      }
      integrationEntityFields.set(
        `${field.fieldId}:${field.entity}:${field.mappingGroupId ?? "default"}`,
        field,
      );

      for (const virkEntity of field.mappingEntityTypes) {
        let virkEntityFields = integrationFieldsByVirkEntity.get(virkEntity);
        if (!virkEntityFields) {
          virkEntityFields = new Map();
          integrationFieldsByVirkEntity.set(virkEntity, virkEntityFields);
        }
        virkEntityFields.set(
          `${field.fieldId}:${field.entity}:${field.mappingGroupId ?? "default"}`,
          field,
        );
      }
    }
    return {
      integrationFieldsByIntegrationEntity,
      integrationFieldsByVirkEntity,
    };
  }, [integrationFields]);

  const { mappingGroups, visibleMappingGroups } = useMemo(() => {
    const mappingGroupMap = new Map<string, string>();
    const visibleMappingGroupMap = new Map<string, string>();

    integrationFields.forEach((field) => {
      if (field.mappingGroupId && field.mappingGroupName) {
        mappingGroupMap.set(field.mappingGroupId, field.mappingGroupName);
        if (field.visible) {
          visibleMappingGroupMap.set(
            field.mappingGroupId,
            field.mappingGroupName,
          );
        }
      }
    });

    return {
      mappingGroups: Array.from(mappingGroupMap).map(([value, label]) => ({
        label,
        value,
      })),
      visibleMappingGroups: Array.from(visibleMappingGroupMap).map(
        ([value, label]) => ({
          label,
          value,
        }),
      ),
    };
  }, [integrationFields]);

  const compositeEntities = useMemo(() => {
    const compositeEntities = new Map<
      string,
      { virkEntity: string; integrationEntity: string }
    >();
    for (const [
      virkEntity,
      integrationFields,
    ] of integrationFieldsByVirkEntity) {
      for (const field of integrationFields.values()) {
        const key = `${virkEntity}:${field.entity}`;
        if (!compositeEntities.has(key)) {
          compositeEntities.set(key, {
            virkEntity,
            integrationEntity: field.entity,
          });
        }
      }
    }
    return compositeEntities;
  }, [integrationFieldsByVirkEntity]);

  const areRequiredFieldsMapped = useMemo(() => {
    const mappingGroupKeys = mappingGroups.length
      ? mappingGroups.map((g) => g.value)
      : ["default"];

    for (const mappingGroup of mappingGroupKeys) {
      const mappingsByCompositeKey = mappings.fieldMappings[mappingGroup];
      if (!mappingsByCompositeKey) continue;

      for (const [compositeKey, mappingsList] of Object.entries(
        mappingsByCompositeKey,
      )) {
        if (!mappingsList?.length) continue;

        const compositeEntity = compositeEntities.get(compositeKey);
        if (!compositeEntity) continue;

        const completeMappings = new Map(
          mappingsList
            .filter(
              (
                m,
              ): m is typeof m & {
                from: ProviderConnectionInfoField;
                to: ProviderConnectionInfoField;
              } => m.from !== null && m.to !== null,
            )
            .map((m) => [`${m.from.entity}:${m.from.fieldId}`, m]),
        );
        if (completeMappings.size === 0) continue;

        const { virkEntity } = compositeEntity;

        const requiredVirkFields = requiredVirkFieldsByEntity.get(virkEntity);

        for (const requiredVirkField of requiredVirkFields?.values() ?? []) {
          if (
            requiredVirkField.visible &&
            !completeMappings.has(
              `${requiredVirkField.entity}:${requiredVirkField.fieldId}`,
            )
          ) {
            return false;
          }
        }
      }
    }

    return true;
  }, [
    compositeEntities,
    mappingGroups,
    mappings.fieldMappings,
    requiredVirkFieldsByEntity,
  ]);

  const areAnyMappingsPresent = useMemo(() => {
    for (const mappingsByCompositeKey of Object.values(
      mappings.fieldMappings,
    )) {
      for (const mappingsList of Object.values(mappingsByCompositeKey || {})) {
        return mappingsList?.some((m) => m.from !== null && m.to !== null);
      }
    }
  }, [mappings.fieldMappings]);

  const presetMappings = useMemo(() => {
    if (mode === "update") {
      return connectionInfo && (connectionInfo.settings?.exportMappings ?? []);
    }
    return defaultMappings;
  }, [mode, connectionInfo, defaultMappings]);

  useEffect(() => {
    if (visibleMappingGroups.length === 1) {
      setMappingGroup(visibleMappingGroups[0].value);
    }
  }, [visibleMappingGroups]);

  // Set default form values for required fields from both sources
  useEffect(() => {
    console.debug("FieldsStep.tsx ~ initialize form useEffect", {
      hasInitializedForm: hasInitializedForm.current,
      presetMappings,
      isLoadingIntegrationFields,
      integrationFieldsLength: integrationFields.length,
      isLoadingVirkFields,
      virkFieldsLength: virkFields.length,
    });
    if (
      hasInitializedForm.current ||
      !presetMappings ||
      isLoadingIntegrationFields ||
      isLoadingVirkFields ||
      !integrationFields.length ||
      !virkFields.length
    )
      return;

    const mappingGroupKeys = mappingGroups.length
      ? mappingGroups.map((g) => g.value)
      : ["default"];

    const newMappingsByMappingGroup = new Map(
      mappingGroupKeys.map((mappingGroupKey) => {
        const newMappingsByCompositeKey = new Map<
          string,
          SingleFieldMapping[]
        >();

        for (const [
          virkEntity,
          integrationFieldsMap,
        ] of integrationFieldsByVirkEntity) {
          for (const integrationField of integrationFieldsMap.values()) {
            const compositeKey = `${virkEntity}:${integrationField.entity}`;
            if (newMappingsByCompositeKey.has(compositeKey)) continue;

            const requiredFields = requiredVirkFieldsByEntity.get(virkEntity);

            newMappingsByCompositeKey.set(
              compositeKey,
              [...(requiredFields?.values() ?? [])].map(
                (field): SingleFieldMapping => ({
                  from: field,
                  to: null,
                  update: false,
                }),
              ),
            );
          }
        }

        return [mappingGroupKey, newMappingsByCompositeKey];
      }),
    );

    function updateMapping(
      virkEntity: string,
      mapping: SingleFieldMapping & {
        from: ProviderConnectionInfoField;
        to: ProviderConnectionInfoField;
      },
    ) {
      const integrationCompositeKey = `${mapping.to?.fieldId}:${mapping.to?.entity}:${mapping.to?.mappingGroupId ?? "default"}`;
      const integrationFieldsById =
        integrationFieldsByVirkEntity.get(virkEntity);
      if (!integrationFieldsById) {
        return;
      }

      const integrationField =
        integrationFieldsById.get(integrationCompositeKey) || null;
      if (!integrationField) {
        return;
      }

      if (mappingGroups.length && !integrationField.mappingGroupId) {
        return;
      }

      const newMappingsByCompositeKey = newMappingsByMappingGroup.get(
        integrationField.mappingGroupId ?? "default",
      );
      if (!newMappingsByCompositeKey) {
        return;
      }

      const compositeKey = `${virkEntity}:${integrationField.entity}`;
      let newMappings = newMappingsByCompositeKey.get(compositeKey);
      if (!newMappings) {
        newMappings = [];
        newMappingsByCompositeKey.set(compositeKey, newMappings);
      }

      let existingIndex = newMappings.findIndex((m) => m.to === mapping.to);

      if (existingIndex === -1) {
        existingIndex = newMappings.findIndex(
          (m) =>
            m.from?.fieldId === mapping.from.fieldId &&
            m.from.entity === virkEntity &&
            !m.to,
        );
      }

      if (existingIndex !== -1) {
        const existingMappings = newMappings[existingIndex];
        newMappings[existingIndex] = {
          ...existingMappings,
          ...mapping,
          to: mapping.to ?? existingMappings.to,
          from: mapping.from ?? existingMappings.from,
          update: mapping.update ?? existingMappings.update,
          locked: mapping.locked ?? existingMappings.locked,
        };
        return;
      }

      newMappings.push(mapping);
    }

    for (const presetMapping of presetMappings) {
      updateMapping(presetMapping.from.entity, {
        from: virkFields.find(
          (f) =>
            f.fieldId === presetMapping.from.fieldId &&
            f.entity === presetMapping.from.entity,
        )!,
        // @ts-expect-error -- Cannot allow nullable in function but presetMapping may have nulls
        to:
          integrationFields.find(
            (f) =>
              f.fieldId === presetMapping.to.fieldId &&
              f.entity === presetMapping.to.entity,
          ) ?? null,
        update: presetMapping.update || false,
        locked: presetMapping.locked || false,
      });
    }

    for (const storedMappings of Object.values(sessionStoredFields)) {
      if (!storedMappings) continue;
      for (const [_, fields] of Object.entries(storedMappings)) {
        fields?.forEach((field) => {
          if (field.to && field.from) {
            const fromField = virkFields.find(
              (f) =>
                f.fieldId === field.from!.fieldId &&
                f.entity === field.from!.entity,
            );
            const toField = integrationFields.find(
              (f) =>
                f.fieldId === field.to!.fieldId &&
                f.entity === field.to!.entity,
            );
            if (!fromField || !toField) return;
            updateMapping(field.from.entity, {
              ...field,
              to: toField,
              from: fromField,
            });
          }
        });
      }
    }

    reset({
      fieldMappings: Object.fromEntries(
        [...newMappingsByMappingGroup].map(
          ([mappingGroupKey, newMappingsByCompositeKey]) => [
            mappingGroupKey,
            Object.fromEntries(newMappingsByCompositeKey),
          ],
        ),
      ),
    });

    console.debug("FieldsStep.tsx ~ initialized form with mappings");

    hasInitializedForm.current = true;
  }, [
    integrationFields,
    virkFields,
    isLoadingIntegrationFields,
    isLoadingVirkFields,
    mappingGroups,
    presetMappings,
    reset,
    integrationFieldsByVirkEntity,
    requiredVirkFieldsByEntity,
    sessionStoredFields,
  ]);

  const onSubmit = async (data: z.infer<typeof fieldsFormSchema>) => {
    if (!connectionInfo) throw new Error("Connection info is missing");

    const updatedConnectionInfo: ProviderConnectionInfo = {
      ...connectionInfo,
      settings: connectionInfo.settings
        ? {
            ...connectionInfo.settings,
            exportMappings: mapExportInputs(
              data.fieldMappings,
              connectionInfo.settings.exportMappings,
            ),
          }
        : { exportMappings: mapExportInputs(data.fieldMappings) },
    };
    setConnectionInfo(updatedConnectionInfo);
    sessionStorage.setItem(
      getSessionKey("fields"),
      JSON.stringify(data.fieldMappings, (_, value) => {
        if (value instanceof Map) {
          return {
            dataType: "Map",
            value: Array.from(value.entries()),
          };
        }
        return value;
      }),
    );
    setPendingStepChange(true);
    onNext?.(updatedConnectionInfo);
    setPendingStepChange(false);
  };

  const handleBack = () => {
    onBack?.();
  };

  return (
    <div ref={containerRef} className="flex h-full w-full flex-col gap-y-7">
      <div className="size-full">
        {hasInitializedForm.current ? (
          <>
            {visibleMappingGroups.length >= 2 && (
              <div className="mx-auto mb-14 max-w-xs min-w-1/2">
                <Select
                  placeholder="Vælg type"
                  aria-label="Vælg type"
                  selectedKey={mappingGroup}
                  onSelectionChange={(key) => setMappingGroup(key as string)}
                  items={visibleMappingGroups}
                >
                  {(item) => (
                    <SelectItem id={item.value}>{item.label}</SelectItem>
                  )}
                </Select>
              </div>
            )}

            {(!visibleMappingGroups.length ||
              (visibleMappingGroups.length && mappingGroup !== null)) && (
              <form
                id="setupForm"
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col justify-between"
              >
                <div className="mb-16">
                  <Controller
                    control={control}
                    name="fieldMappings"
                    render={({ field }) => (
                      <FieldMappings
                        virkFields={virkFields}
                        virkFieldsByEntity={virkFieldsByEntity}
                        integrationFields={integrationFields}
                        integrationFieldsByVirkEntity={
                          integrationFieldsByVirkEntity
                        }
                        mappingGroups={visibleMappingGroups}
                        mappingGroup={mappingGroup}
                        value={field.value[mappingGroup ?? "default"] ?? {}}
                        onDelete={(value) => {
                          const currentMappings =
                            field.value[mappingGroup ?? "default"] ?? {};
                          const newMappingsByCompositeKey = new Map(
                            Object.entries(currentMappings).map(
                              ([compositeKey, mappingsList]) => [
                                compositeKey,
                                mappingsList ? [...mappingsList] : [],
                              ],
                            ),
                          );

                          for (const [
                            compositeKey,
                            mappingsList,
                          ] of Object.entries(currentMappings)) {
                            const newMappings = mappingsList?.filter(
                              (m) =>
                                !(
                                  m.from === value.from &&
                                  m.to === value.to &&
                                  m.update === value.update
                                ),
                            );
                            newMappingsByCompositeKey.set(
                              compositeKey,
                              newMappings ? [...newMappings] : [],
                            );
                          }

                          field.onChange({
                            ...field.value,
                            [mappingGroup ?? "default"]: Object.fromEntries(
                              newMappingsByCompositeKey,
                            ),
                          } satisfies FieldsForm["fieldMappings"]);
                        }}
                        onChange={(newValue) => {
                          field.onChange({
                            ...field.value,
                            [mappingGroup ?? "default"]: newValue,
                          } satisfies FieldsForm["fieldMappings"]);
                        }}
                      />
                    )}
                  />
                </div>

                {/* Navigation buttons */}
                <StickyElement
                  stickyProps={{
                    className: "bg-theme-background -mx-6 xl:-mx-14",
                  }}
                  ref={stickyRef}
                  side="bottom"
                >
                  <div className="mt-auto flex justify-center gap-x-4 py-4">
                    {mode === "setup" && onBack && (
                      <Button
                        type="button"
                        intent="tertiary"
                        onClick={handleBack}
                      >
                        Tilbage
                      </Button>
                    )}
                    <Button
                      type="submit"
                      intent="primary"
                      isDisabled={
                        !isValid ||
                        !areRequiredFieldsMapped ||
                        !areAnyMappingsPresent ||
                        pendingStepChange
                      }
                    >
                      {pendingStepChange && (
                        <LoaderCircle className="mr-2 size-4 shrink-0 animate-spin" />
                      )}
                      {connectMutation.isPending
                        ? "Gemmer..."
                        : "Gem ændringer"}
                    </Button>
                  </div>
                </StickyElement>
              </form>
            )}
          </>
        ) : (
          <div className="flex size-full flex-col items-center justify-center gap-y-4">
            <Loader2 className="size-6 animate-spin" />
            <div>Indlæser felter...</div>
          </div>
        )}
      </div>
    </div>
  );
}
